import { useRouter } from 'next/router';
import ProjectPageTemplate from './template';
import smartTrim from '../../../utils/wordsRemove';
import { ProjectProjectsDynamicDynamicZone, Technology, Testimony } from '../../../types/schema';
import { ArticleJsonLd, BreadcrumbJsonLd, NextSeo } from 'next-seo';
import { breadcrumbTitle, SEO } from '../../../utils/SEO';
import transformUrl from '../../../utils/transformURL';

type ProjectPageProps = {
  metaTitle: string;
  metaDescription: string;
  metaImage: string;
  hideAppMastersTitle?: boolean;
  slug: string;
  projectsDynamic: ProjectProjectsDynamicDynamicZone[];
  image: string;
  seoImage?: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  description: string;
  technologies?: Technology[];
  testimonies: Testimony[];
};

const ProjectPageComponent: React.FC<ProjectPageProps> = ({
  metaTitle,
  metaDescription,
  metaImage,
  hideAppMastersTitle,
  slug,
  description,
  createdAt,
  updatedAt,
  ...props
}) => {
  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || 'http://localhost:3000/';
  const router = useRouter();
  const isEn = router.locale === 'en';
  const openGraphImage =
    (metaImage &&
      (metaImage.startsWith('http') ? metaImage : transformUrl(metaImage, 'projectBanner'))) ||
    props.seoImage ||
    `${siteUrl}appmasters-metaimage.png`;

  const itemListElements = [
    {
      position: 1,
      name: 'App Masters',
      item: `${siteUrl}${router.locale}`,
    },
    {
      position: 2,
      name: breadcrumbTitle(isEn ? 'Projects' : 'Projetos', isEn),
      item: `${siteUrl}${router.locale}/${isEn ? 'projects' : 'projetos'}`,
    },
    {
      position: 3,
      name: breadcrumbTitle(metaTitle || props.title, isEn, hideAppMastersTitle),
      item: `${siteUrl}${router.locale}/${isEn ? 'projects' : 'projetos'}/${slug}`,
    },
  ];

  const article = {
    url: `${siteUrl}${router.locale}/${isEn ? 'projects' : 'projetos'}/${slug}`,
    title: props.title,
    images: [openGraphImage],
    datePublished: createdAt,
    dateModified: updatedAt,
    authorName: 'App Masters',
    publisherName: 'App Masters',
    publisherLogo: `${process.env.NEXT_PUBLIC_SITE_URL}appmasters-metaimage.png`,
    description: smartTrim(description as string, 165, ' ', '...'),
  };

  return (
    <>
      <NextSeo
        {...SEO({
          pathname: router.asPath,
          locale: router.locale as string,
          title: (metaTitle || props.title) as string,
          defaultTitle: {
            enTitle: 'Project',
            ptTitle: 'Projeto',
          },
          description: (metaDescription || description) as string,
          image: openGraphImage as string,
          hideAppMastersTitle: hideAppMastersTitle as boolean,
        })}
      />
      <BreadcrumbJsonLd itemListElements={itemListElements} />
      <ArticleJsonLd {...article} />
      <ProjectPageTemplate {...props} />
    </>
  );
};

export default ProjectPageComponent;
